import { Injectable } from "@angular/core";
import { UserDetailsService } from "@users/services/user-details/user-details.service";
import {
  loadUserDetails,
  loadUserDetailsFailure,
  loadUserDetailsSuccess,
  loadUserDetailsNotFound,
  deleteUserDetails,
  deleteUserDetailsFailure,
  deleteUserDetailsSuccess,
  enableUser,
  enableUserFailure,
  enableUserSuccess,
  disableUser,
  disableUserFailure,
  disableUserSuccess,
  resetAppCode,
  resetAppCodeFailure,
  resetAppCodeSuccess,
  resetWebauthn,
  resetWebauthnFailure,
  resetWebauthnSuccess
} from "@users/store/actions/user-details.actions";
import { NotificationService } from "@core/services";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, map, of } from "rxjs";

@Injectable()
export class UserDetailsEffects {
  loadUserDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadUserDetails),
      concatMap(({ userDetailsId }) =>
        this.userDetailsService.getUserDetails(userDetailsId).pipe(
          map((userDetails) => loadUserDetailsSuccess({ userDetails })),
          catchError((error) => {
            if (error.status === 404) {
              return of(loadUserDetailsNotFound());
            }
            return of(loadUserDetailsFailure());
          })
        )
      )
    );
  });

  loadUserDetailsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loadUserDetailsFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_load_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  loadUserDetailsNotFound$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loadUserDetailsNotFound),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_not_found",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  deleteUserDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteUserDetails),
      concatMap(({ userDetails }) =>
        this.userDetailsService.deleteUser(userDetails).pipe(
          map(() => deleteUserDetailsSuccess({ userDetailsId: userDetails.id })),
          catchError(() => of(deleteUserDetailsFailure()))
        )
      )
    );
  });

  deleteUserDetailsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteUserDetailsSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_delete_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  deleteUserDetailsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteUserDetailsFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_delete_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  enableUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(enableUser),
      concatMap(({ userId }) =>
        this.userDetailsService.enableUser(userId).pipe(
          map(() => enableUserSuccess({ userId })),
          catchError(() => of(enableUserFailure()))
        )
      )
    );
  });

  enableUserSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(enableUserSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_enable_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  enableUserFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(enableUserFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_enable_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  disableUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(disableUser),
      concatMap(({ userId }) =>
        this.userDetailsService.disableUser(userId).pipe(
          map(() => disableUserSuccess({ userId })),
          catchError(() => of(disableUserFailure()))
        )
      )
    );
  });

  disableUserSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(disableUserSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_disable_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  disableUserFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(disableUserFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_disable_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  resetAppCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetAppCode),
      concatMap(({ userId }) =>
        this.userDetailsService.resetAppCode(userId).pipe(
          map(() => resetAppCodeSuccess({ userId })),
          catchError(() => of(resetAppCodeFailure()))
        )
      )
    );
  });

  resetAppCodeSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resetAppCodeSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_reset_app_code_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  resetAppCodeFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resetAppCodeFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_reset_app_code_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  resetWebauthn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetWebauthn),
      concatMap(({ userId }) =>
        this.userDetailsService.resetWebauthn(userId).pipe(
          map(() => resetWebauthnSuccess({ userId })),
          catchError(() => of(resetWebauthnFailure()))
        )
      )
    );
  });

  resetWebauthnSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resetWebauthnSuccess),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_reset_passkey_success",
            "confirmation"
          )
        )
      );
    },
    { dispatch: false }
  );

  resetWebauthnFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(resetWebauthnFailure),
        map(() =>
          this.notificationService.showTranslatedNotification(
            "notifications.userDetails_reset_passkey_failure",
            "warning"
          )
        )
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private userDetailsService: UserDetailsService
  ) {}
}
